import { api } from '../../shared/api';
import { useState, useRef } from 'react';
import { FileSpreadsheet, Upload, X, Download, AlertCircle, CheckCircle2 } from 'lucide-react';

type Status = 'idle' | 'processing' | 'done' | 'error';

export function PfFileGenNewPage() {
  const [file,       setFile]       = useState<File | null>(null);
  const [status,     setStatus]     = useState<Status>('idle');
  const [errorMsg,   setErrorMsg]   = useState<string | null>(null);
  const [outputUrl,  setOutputUrl]  = useState<string | null>(null);
  const [outputName, setOutputName] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function reset() { setStatus('idle'); setErrorMsg(null); setOutputUrl(null); setOutputName(null); }

  async function handleSubmit() {
    if (!file) return;
    reset();
    setStatus('processing');
    try {
      const form = new FormData();
      form.append('bo_file', file);
      const response = await api.post('/api/pf-file-gen-new/generate', form, { responseType: 'blob' });
      const blob = response.data as Blob;
      const url  = URL.createObjectURL(blob);
      setOutputUrl(url);
      // Use the server-provided filename (PF_<ClientID>.xlsx from the CMOTS file).
      const disposition = String(response.headers?.['content-disposition'] ?? '');
      const nameMatch = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\r\n]+)/i);
      setOutputName(nameMatch ? decodeURIComponent(nameMatch[1].trim()) : 'pf_ruled.xlsx');
      setStatus('done');
    } catch (err: unknown) {
      let msg = 'Unknown error';
      const axiosErr = err as { response?: { data?: unknown }; message?: string };
      const data = axiosErr?.response?.data;
      if (data instanceof Blob) {
        try { const t = await data.text(); msg = JSON.parse(t)?.error ?? t ?? msg; } catch { msg = axiosErr.message ?? msg; }
      } else if (err instanceof Error) { msg = err.message; }
      setErrorMsg(msg);
      setStatus('error');
    }
  }

  const ready = !!file && status !== 'processing';

  return (
    <div className="max-w-xl mx-auto px-4 py-10">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--text-strong)' }}>PF File Generator</h1>
        <p className="mt-1.5 text-sm" style={{ color: 'var(--text-200)' }}>
          Upload the CMOTS transaction file. Rules will be applied and a <code>PF_&lt;ClientID&gt;.xlsx</code> file (Client ID read from the CMOTS file) will be generated for download.
        </p>
      </div>

      <div className="rounded-2xl p-6 space-y-5" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)', boxShadow: 'inset 0 1px 0 0 var(--glass-edge)' }}>
        {/* File slot */}
        <div>
          <div className="text-sm font-medium mb-1.5" style={{ color: 'var(--text-strong)' }}>CMOTS File (.xlsx)</div>
          <div className="text-xs mb-2" style={{ color: 'var(--text-400)' }}>The CMOTS transaction file with ISIN, TRADE_DATE, BUY_SELL_FLAG, etc.</div>
          <label className="flex items-center gap-3 p-3.5 rounded-xl cursor-pointer transition"
            style={{ border: `1.5px dashed ${file ? 'var(--accent)' : 'var(--glass-border-h)'}`, background: file ? 'color-mix(in srgb, var(--accent) 6%, transparent)' : 'var(--bg-surface)' }}>
            <FileSpreadsheet className="w-5 h-5 shrink-0" style={{ color: 'var(--accent)' }} />
            {file ? (
              <div className="flex flex-1 items-center gap-3 min-w-0">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: 'var(--text-strong)' }}>{file.name}</div>
                  <div className="text-xs mt-0.5" style={{ color: 'var(--text-200)' }}>{(file.size / 1024).toFixed(1)} KB</div>
                </div>
                <button type="button" onClick={e => { e.preventDefault(); setFile(null); reset(); if (fileRef.current) fileRef.current.value = ''; }}
                  className="shrink-0 p-1 rounded-lg" style={{ color: 'var(--text-400)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--status-err)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--text-400)'; }}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <span className="text-sm" style={{ color: 'var(--text-400)' }}>Click to select a file</span>
            )}
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
              onChange={e => { setFile(e.target.files?.[0] ?? null); reset(); }} />
          </label>
        </div>

        {status === 'error' && errorMsg && (
          <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-xl text-sm"
            style={{ background: 'color-mix(in srgb, var(--status-err) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--status-err) 30%, transparent)', color: 'var(--status-err)' }}>
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{errorMsg}</span>
          </div>
        )}

        <button type="button" disabled={!ready} onClick={handleSubmit}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium transition"
          style={{ background: ready ? 'linear-gradient(135deg, var(--accent) 0%, #e0336a 100%)' : 'var(--bg-hover)', color: ready ? '#fff' : 'var(--text-400)', cursor: ready ? 'pointer' : 'not-allowed', boxShadow: ready ? '0 4px 14px 0 var(--accent-soft)' : 'none' }}>
          {status === 'processing' ? (
            <><div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />Generating…</>
          ) : (
            <><Upload className="w-4 h-4" />Generate PF file</>
          )}
        </button>
      </div>

      {status === 'done' && outputUrl && outputName && (
        <div className="mt-5 rounded-2xl p-5"
          style={{ background: 'color-mix(in srgb, var(--status-ok) 8%, var(--bg-elevated))', border: '1px solid color-mix(in srgb, var(--status-ok) 35%, transparent)' }}>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'color-mix(in srgb, var(--status-ok) 15%, transparent)' }}>
              <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--status-ok)' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium" style={{ color: 'var(--text-strong)' }}>{outputName}</div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--status-ok)' }}>PF file generated successfully</div>
            </div>
            <a href={outputUrl} download={outputName}
              className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium"
              style={{ background: 'var(--status-ok)', color: '#fff', boxShadow: '0 2px 8px 0 rgba(0,180,100,0.25)', textDecoration: 'none' }}>
              <Download className="w-3.5 h-3.5" />Download
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
